import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { OrgChart } from 'd3-org-chart';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36', surfaceLighter: '#383842',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
};

// User-type accent palette for org chart cards.
// Goals: (1) HR can scan the chart and instantly see which cards are non-
// Employee, (2) the four non-Employee types are visually distinct from each
// other for colorblind-safe hue separation (amber / teal / green / gray sit
// far apart on the hue wheel), and (3) accents stay legible on the dark
// surface — desaturated mid-tones, not full-saturation primaries.
//
// Used for the left-edge accent stripe and the user_type pill background
// (low-opacity rgba). Employee falls back to the existing purple chrome so
// the default majority of cards keep the current look.
const USER_TYPE_ACCENT = {
  Employee:   { solid: B.purple,    rgba: 'rgba(123, 92, 200, 0.18)' },
  Contractor: { solid: '#D49A3A',   rgba: 'rgba(212, 154, 58, 0.18)' }, // amber
  Consultant: { solid: '#3FB8C9',   rgba: 'rgba(63, 184, 201, 0.18)' }, // teal
  Intern:     { solid: '#5BB870',   rgba: 'rgba(91, 184, 112, 0.18)' }, // green
  Advisor:    { solid: '#9090a0',   rgba: 'rgba(144, 144, 160, 0.20)' },// gray
};
function accentFor(userType) {
  return USER_TYPE_ACCENT[userType] || USER_TYPE_ACCENT.Employee;
}

function initials(name) {
  return (name || '').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// HTML-escape values that get interpolated into d3-org-chart's nodeContent
// (which sets innerHTML). Without this a malicious value in the DB could
// inject markup/script.
function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Allowlist photo URLs so a hostile DB value can't smuggle a javascript:
// or data: URL into an <img src>. Only https:// is accepted.
function safePhotoUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function buildNodes(employees) {
  const emailToId = new Map(employees.map(e => [e.email.toLowerCase(), e.id]));
  return employees.map(e => ({
    id: e.id,
    parentId: emailToId.get((e.manager_email || '').toLowerCase()) || null,
    name: e.name,
    title: e.title || '',
    department: e.department || '',
    office_location: e.office_location || '',
    user_type: e.user_type || 'Employee',
    photo_url: safePhotoUrl(e.photo_url), // forward-compat: render <img> when populated
    isNonEmployee: (e.user_type || 'Employee') !== 'Employee',
    _highlighted: false,
  }));
}

export default function OrgChartPage() {
  const navigate = useNavigate();
  const { employee } = useUser();
  const { oktaAuth } = useOktaAuth();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      // org_chart_v is a display-only view that bypasses employees RLS so
      // every authenticated user sees the full company tree. See db/schema.sql.
      const { data, error } = await supabase
        .from('org_chart_v')
        .select('id,name,email,title,department,manager_email,user_type,office_location');
      if (!active) return;
      if (!error && data) setEmployees(data);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (loading || !employees.length || !containerRef.current) return;

    const nodes = buildNodes(employees);

    // d3-org-chart renders a single connected tree. If multiple users have
    // no manager (e.g., CEO + a contractor without a manager_email), inject
    // a hidden virtual root so the orphans still appear.
    const orphans = nodes.filter(n => !n.parentId);
    let renderNodes = nodes;
    if (orphans.length > 1) {
      renderNodes = [
        { id: '__root__', parentId: null, name: 'xLight', title: '', department: '', user_type: 'Employee', isNonEmployee: false, _hidden: true },
        ...nodes.map(n => n.parentId ? n : { ...n, parentId: '__root__' }),
      ];
    }

    if (!chartRef.current) chartRef.current = new OrgChart();

    chartRef.current
      .container(containerRef.current)
      .data(renderNodes)
      .nodeWidth(() => 260)
      .nodeHeight(() => 124)
      .childrenMargin(() => 50)
      .compactMarginBetween(() => 35)
      .compactMarginPair(() => 30)
      .neighbourMargin(() => 25)
      .nodeContent(d => {
        if (d.data._hidden) return `<div style="width:0;height:0;"></div>`;
        const isNonEmployee = d.data.isNonEmployee;
        const highlighted = d.data._highlighted;
        const accent = accentFor(d.data.user_type);
        // Highlighted (search match) takes precedence on all four sides:
        // the main border AND the left stripe both go gold so the highlight
        // is visually consistent. Otherwise the card border stays subtle
        // (borderLight) and the accent appears as a left stripe so
        // colour-coding doesn't dominate the layout.
        const borderColor = highlighted ? B.gold : B.borderLight;
        const leftStripeColor = highlighted ? B.gold : accent.solid;
        // Card body uses surfaceLighter (one shade up from the previous
        // surfaceLight) so textSecondary / textMuted are easier to read.
        const bg = B.surfaceLighter;
        // All DB-derived strings are HTML-escaped; photo_url is allowlisted to https:// in buildNodes.
        const avatarInner = d.data.photo_url
          ? `<img src="${esc(d.data.photo_url)}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" />`
          : esc(initials(d.data.name));
        // Pill is shown for every non-Employee type so HR can scan the chart
        // and immediately see who is Contractor / Consultant / Intern / Advisor.
        const pill = isNonEmployee
          ? `<span style="display:inline-block;font-size:10px;font-weight:600;letter-spacing:0.04em;color:${accent.solid};background:${accent.rgba};padding:2px 7px;border-radius:10px;margin-left:6px;vertical-align:middle;white-space:nowrap;">${esc(d.data.user_type)}</span>`
          : '';
        const locationLine = d.data.office_location
          ? `<div style="font-size:10px;color:${B.textSecondary};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;">${esc(d.data.office_location)}</div>`
          : '';
        return `
          <div style="background:${bg};border:1.5px solid ${borderColor};border-left:4px solid ${leftStripeColor};border-radius:8px;padding:12px 12px 12px 14px;height:100px;display:flex;gap:10px;color:${B.textPrimary};font-family:inherit;box-sizing:border-box;${highlighted ? 'box-shadow:0 0 0 2px ' + B.gold : ''}">
            <div style="width:42px;height:42px;border-radius:50%;background:${B.purpleDeep};border:1px solid ${B.purpleDim};display:flex;align-items:center;justify-content:center;font-weight:600;font-size:13px;color:${B.purple};flex-shrink:0;overflow:hidden;">${avatarInner}</div>
            <div style="overflow:hidden;flex:1;min-width:0;">
              <div style="font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(d.data.name)}${pill}</div>
              <div style="font-size:11px;color:${B.textSecondary};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;">${esc(d.data.title || ' ')}</div>
              ${locationLine}
              <div style="font-size:10px;color:${B.textSecondary};margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(d.data.department || '')}</div>
            </div>
          </div>
        `;
      })
      .render();
  }, [employees, loading]);

  function onSearch(e) {
    const q = e.target.value;
    setSearch(q);
    const chart = chartRef.current;
    if (!chart) return;
    const lower = q.trim().toLowerCase();
    const data = chart.data();
    const matched = [];
    data.forEach(n => {
      const isMatch = lower.length > 0 && (
        (n.name || '').toLowerCase().includes(lower) ||
        (n.title || '').toLowerCase().includes(lower) ||
        (n.department || '').toLowerCase().includes(lower)
      );
      n._highlighted = isMatch;
      if (isMatch) matched.push(n);
    });
    // Single render per keystroke — d3-org-chart's setters chain, so we
    // stage data + (optional) center together and render once.
    chart.data(data);
    if (matched.length > 0) chart.setCentered(matched[0].id);
    chart.render();
  }

  return (
    <div style={{ background: B.black, minHeight: '100vh', color: B.textPrimary }}>
      <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/')} aria-label="Back to portal" style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
            <span style={{ fontSize: 13, color: B.textSecondary, letterSpacing: '0.06em' }}>myhr</span>
          </button>
          <span style={{ width: 1, height: 16, background: B.border, margin: '0 4px' }} />
          <button onClick={() => navigate('/')} style={{ fontSize: 11, color: B.textSecondary, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>BACK TO PORTAL</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: B.textSecondary }}>org chart</span>
          <button type="button" onClick={() => oktaAuth.signOut()} aria-label="Sign out" style={{ width: 32, height: 32, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: B.purple, cursor: 'pointer', padding: 0 }}>
            {initials(employee?.name) || 'ME'}
          </button>
        </div>
      </div>

      <div style={{ padding: '14px 28px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: `0.5px solid ${B.border}`, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={onSearch}
          placeholder="Search by name, title, or department"
          aria-label="Search org chart"
          style={{ background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', color: B.textPrimary, fontSize: 13, minWidth: 320, outline: 'none' }} />
        <button onClick={() => chartRef.current?.fit()} style={{ fontSize: 11, color: B.textSecondary, background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer', letterSpacing: '0.05em' }}>FIT</button>
        <button onClick={() => chartRef.current?.expandAll().fit()} style={{ fontSize: 11, color: B.textSecondary, background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer', letterSpacing: '0.05em' }}>EXPAND ALL</button>
        <button onClick={() => chartRef.current?.collapseAll().fit()} style={{ fontSize: 11, color: B.textSecondary, background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer', letterSpacing: '0.05em' }}>COLLAPSE ALL</button>
        <span style={{ fontSize: 11, color: B.textSecondary, marginLeft: 'auto' }}>{employees.length} people · non-employees show a coloured accent + type pill</span>
      </div>

      <div ref={containerRef} style={{ width: '100%', height: 'calc(100vh - 110px)' }} />

      {loading && <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: B.textSecondary, fontSize: 13, letterSpacing: '0.06em' }}>LOADING...</div>}
    </div>
  );
}
